import { useEffect, useMemo, useState } from "react";
import { readSheet } from "read-excel-file/universal";
import type { PlatformClient } from "../../platform/supabase";

type Tab="IMPORT"|"ROSTER";
type ImportRow={studentKey:string;displayName:string;gradeLevel:string;classLabel:string;email:string};
type ImportResult={
  runId:string;status:string;requestedCount:number;createdCount:number;
  updatedCount:number;reactivatedCount:number;skippedCount:number;failedCount:number;
};
type AcademicYear={academicYearId:string;yearCode:string;status:string;startsOn:string;endsOn:string};
type TargetClass={
  classGroupId:string;academicYearId:string;yearCode:string;gradeLevel:string;
  classLabel:string;name:string;capacity?:number|null;plannedCount:number;
};
type PlanItem={
  itemId:string;learnerId:string;displayName:string;action:"PROMOTE"|"REPEAT"|"GRADUATE";
  sourceGradeLevel?:string|null;sourceClassLabel?:string|null;
  targetClassGroupId?:string|null;targetGradeLevel?:string|null;targetClassLabel?:string|null;
  status:string;
};
type Plan={
  planId:string;name:string;status:string;sourceAcademicYearId:string;sourceYearCode:string;
  targetAcademicYearId:string;targetYearCode:string;transitionBatchId?:string|null;
  createdAt:string;preparedAt?:string|null;items:PlanItem[];
};
type ImportRun={
  runId:string;sourceType:string;status:string;requestedCount:number;createdCount:number;
  updatedCount:number;reactivatedCount:number;failedCount:number;createdAt:string;
};
type Workspace={
  activeAcademicYearId?:string|null;
  academicYears:AcademicYear[];
  targetClasses:TargetClass[];
  plans:Plan[];
  importRuns:ImportRun[];
};

const HEADER_ALIASES:Record<string,keyof ImportRow>={
  "학생코드":"studentKey","학번":"studentKey","studentkey":"studentKey","studentid":"studentKey","id":"studentKey",
  "학생이름":"displayName","이름":"displayName","성명":"displayName","displayname":"displayName","name":"displayName",
  "학년":"gradeLevel","grade":"gradeLevel","gradelevel":"gradeLevel",
  "반":"classLabel","학급":"classLabel","class":"classLabel","classlabel":"classLabel",
  "이메일":"email","메일":"email","email":"email"
};

function normalizeHeader(value:unknown){
  return String(value ?? "").replace(/[\s_-]+/g,"").toLowerCase();
}

function parseCsv(text:string){
  const rows:string[][]=[];
  let row:string[]=[];
  let cell="";
  let quoted=false;
  const input=text.replace(/^\uFEFF/,"");
  for(let i=0;i<input.length;i++){
    const ch=input[i];
    if(ch==='"'){
      if(quoted && input[i+1]==='"'){cell+='"';i++;}
      else quoted=!quoted;
    }else if(ch==="," && !quoted){
      row.push(cell);cell="";
    }else if((ch==="\n" || ch==="\r") && !quoted){
      if(ch==="\r" && input[i+1]==="\n")i++;
      row.push(cell);cell="";
      if(row.some((value)=>value.trim()!==""))rows.push(row);
      row=[];
    }else{
      cell+=ch;
    }
  }
  row.push(cell);
  if(row.some((value)=>value.trim()!==""))rows.push(row);
  return rows;
}

function matrixToRows(matrix:unknown[][]):ImportRow[]{
  if(matrix.length<2)return [];
  const headers=matrix[0].map((value)=>HEADER_ALIASES[normalizeHeader(value)] || null);
  const required=["studentKey","displayName","gradeLevel"] as Array<keyof ImportRow>;
  for(const field of required){
    if(!headers.includes(field))throw new Error("필수 열이 없습니다: 학생코드/학번, 학생이름/이름, 학년");
  }
  return matrix.slice(1).flatMap((cells)=>{
    const row:ImportRow={studentKey:"",displayName:"",gradeLevel:"",classLabel:"",email:""};
    headers.forEach((field,index)=>{
      if(field)row[field]=String(cells[index] ?? "").trim();
    });
    return Object.values(row).some((value)=>value!=="") ? [row] : [];
  });
}

function date(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

export function CenterRosterPlanningPage({client,schoolId}:{client:PlatformClient;schoolId:string}){
  const [tab,setTab]=useState<Tab>("IMPORT");
  const [data,setData]=useState<Workspace|null>(null);
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");

  const [fileName,setFileName]=useState("");
  const [sourceType,setSourceType]=useState("CSV_IMPORT");
  const [importRows,setImportRows]=useState<ImportRow[]>([]);
  const [importResult,setImportResult]=useState<ImportResult|null>(null);

  const [yearCode,setYearCode]=useState("");
  const [yearStart,setYearStart]=useState("");
  const [yearEnd,setYearEnd]=useState("");
  const [targetYearId,setTargetYearId]=useState("");
  const [classGrade,setClassGrade]=useState("");
  const [classLabel,setClassLabel]=useState("");
  const [className,setClassName]=useState("");
  const [classCapacity,setClassCapacity]=useState("30");
  const [planName,setPlanName]=useState("");
  const [selectedPlanId,setSelectedPlanId]=useState("");
  const [selectedLearners,setSelectedLearners]=useState<string[]>([]);
  const [bulkTargetClass,setBulkTargetClass]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_roster_planning_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setData(next);
      const planned=next.academicYears.find((item)=>item.status==="PLANNED");
      setTargetYearId((current)=>next.academicYears.some((item)=>item.academicYearId===current && item.status==="PLANNED") ? current : planned?.academicYearId || "");
      setSelectedPlanId((current)=>next.plans.some((item)=>item.planId===current) ? current : next.plans.find((item)=>["DRAFT","PREPARED"].includes(item.status))?.planId || next.plans[0]?.planId || "");
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedPlan=useMemo(
    ()=>data?.plans.find((item)=>item.planId===selectedPlanId) || null,
    [data,selectedPlanId]
  );
  const plannedYears=useMemo(
    ()=>data?.academicYears.filter((item)=>item.status==="PLANNED") || [],
    [data]
  );
  const targetClasses=useMemo(
    ()=>data?.targetClasses.filter((item)=>!targetYearId || item.academicYearId===targetYearId) || [],
    [data,targetYearId]
  );
  const planClasses=useMemo(
    ()=>data?.targetClasses.filter((item)=>item.academicYearId===selectedPlan?.targetAcademicYearId) || [],
    [data,selectedPlan?.targetAcademicYearId]
  );

  async function run(key:string,fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await fn();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  async function chooseFile(file?:File){
    if(!file)return;
    setError("");setMessage("");setImportResult(null);
    try{
      const lower=file.name.toLowerCase();
      let matrix:unknown[][];
      if(lower.endsWith(".xlsx")){
        matrix=await readSheet(file) as unknown[][];
        setSourceType("XLSX_IMPORT");
      }else if(lower.endsWith(".csv")){
        matrix=parseCsv(await file.text());
        setSourceType("CSV_IMPORT");
      }else{
        throw new Error("CSV 또는 XLSX 파일만 사용할 수 있습니다.");
      }
      const rows=matrixToRows(matrix);
      if(!rows.length)throw new Error("가져올 학생 행이 없습니다.");
      if(rows.length>1000)throw new Error("한 번에 최대 1,000명까지 가져올 수 있습니다.");
      setFileName(file.name);
      setImportRows(rows);
      setMessage(`${rows.length}개 행을 읽었습니다. 아래 미리보기를 확인한 뒤 적용하세요.`);
    }catch(err){
      setFileName("");setImportRows([]);
      setError(err instanceof Error ? err.message : "파일을 읽지 못했습니다.");
    }
  }

  async function applyImport(){
    const result=await run("import",()=>client.rpc("center_import_student_roster",{
      p_school_id:schoolId,p_rows:importRows,p_source_type:sourceType
    }));
    if(result){
      const value=result as ImportResult;
      setImportResult(value);
      setMessage(`명단 동기화 완료 · 신규 ${value.createdCount} / 수정 ${value.updatedCount} / 재등록 ${value.reactivatedCount} / 실패 ${value.failedCount}`);
      setImportRows([]);setFileName("");
    }
  }

  async function createYear(){
    const id=await run("year",()=>client.rpc("center_create_academic_year",{
      p_school_id:schoolId,p_year_code:yearCode,p_starts_on:yearStart,p_ends_on:yearEnd
    }),"다음 학년도를 만들었습니다.");
    if(id){setTargetYearId(String(id));setYearCode("");setYearStart("");setYearEnd("");}
  }

  async function createClass(){
    const id=await run("class",()=>client.rpc("center_create_class_group",{
      p_school_id:schoolId,p_academic_year_id:targetYearId,
      p_grade_level:classGrade,p_class_label:classLabel,
      p_name:className.trim() || null,p_capacity:classCapacity ? Number(classCapacity) : null
    }),"차년도 학급을 만들었습니다.");
    if(id){setClassGrade("");setClassLabel("");setClassName("");}
  }

  async function createPlan(){
    const result=await run("plan",()=>client.rpc("center_create_roster_plan",{
      p_school_id:schoolId,p_target_academic_year_id:targetYearId,p_name:planName.trim() || null
    }),"현재 재학생 기준 반편성안을 만들었습니다.");
    if(result){
      const value=result as {planId:string};
      setSelectedPlanId(value.planId);setPlanName("");setTab("ROSTER");
    }
  }

  async function autoAssign(){
    if(!selectedPlan)return;
    const result=await run("auto",()=>client.rpc("center_auto_assign_roster_plan",{
      p_school_id:schoolId,p_plan_id:selectedPlan.planId
    }));
    if(result){
      const value=result as {assignedCount:number;unassignedCount:number};
      setMessage(`같은 반 번호 자동배정 ${value.assignedCount}명 · 수동배정 필요 ${value.unassignedCount}명`);
    }
  }

  async function setItemTarget(item:PlanItem,value:string){
    if(!selectedPlan)return;
    if(value==="GRADUATE"){
      await run("item-"+item.learnerId,()=>client.rpc("center_update_roster_plan_item",{
        p_school_id:schoolId,p_plan_id:selectedPlan.planId,p_learner_id:item.learnerId,
        p_action:"GRADUATE",p_target_class_group_id:null
      }));
      return;
    }
    const target=planClasses.find((group)=>group.classGroupId===value);
    if(!target)return;
    const source=Number(item.sourceGradeLevel);
    const targetGrade=Number(target.gradeLevel);
    const action=targetGrade===source ? "REPEAT" : targetGrade===source+1 ? "PROMOTE" : "";
    if(!action){setError("현재 학년과 같거나 바로 다음 학년의 반만 선택할 수 있습니다.");return;}
    await run("item-"+item.learnerId,()=>client.rpc("center_update_roster_plan_item",{
      p_school_id:schoolId,p_plan_id:selectedPlan.planId,p_learner_id:item.learnerId,
      p_action:action,p_target_class_group_id:target.classGroupId
    }));
  }

  async function bulkAssign(){
    if(!selectedPlan || !selectedLearners.length || !bulkTargetClass)return;
    const result=await run("bulk-class",()=>client.rpc("center_bulk_assign_roster_class",{
      p_school_id:schoolId,p_plan_id:selectedPlan.planId,
      p_learner_ids:selectedLearners,p_target_class_group_id:bulkTargetClass
    }),"선택 학생의 차년도 반을 일괄배정했습니다.");
    if(result)setSelectedLearners([]);
  }

  async function preparePlan(){
    if(!selectedPlan)return;
    await run("prepare",()=>client.rpc("center_prepare_roster_plan",{
      p_school_id:schoolId,p_plan_id:selectedPlan.planId
    }),"반편성안을 학년이동 준비안으로 확정했습니다.");
  }

  if(!data)return <p className="muted">명단·반편성 ERP를 불러오는 중…</p>;

  const readyCount=selectedPlan?.items.filter((item)=>item.status==="READY" || item.status==="PREPARED").length || 0;
  const allReady=Boolean(selectedPlan?.items.length && readyCount===selectedPlan.items.length);

  return (
    <section className="roster-erp-page">
      <header className="roster-erp-head">
        <div><p className="eyebrow">Roster ERP</p><h2>명단·반편성</h2><p>학생명단 동기화와 차년도 반편성안을 Student Master 기준으로 처리합니다.</p></div>
        <div className="roster-year-chip"><span>현재 학년도</span><strong>{data.academicYears.find((item)=>item.academicYearId===data.activeAcademicYearId)?.yearCode || "-"}</strong></div>
      </header>
      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <nav className="roster-erp-tabs">
        <button className={tab==="IMPORT"?"active":""} onClick={()=>setTab("IMPORT")}>학생명단 가져오기</button>
        <button className={tab==="ROSTER"?"active":""} onClick={()=>setTab("ROSTER")}>차년도 반편성</button>
      </nav>

      {tab==="IMPORT" && (
        <div className="roster-import-layout">
          <section className="roster-card">
            <div className="panel-title"><div><strong>CSV / XLSX 명단 동기화</strong><span>신규 학생은 생성하고, 같은 학생코드는 기존 Student Master를 갱신합니다.</span></div></div>
            <div className="import-format-note">
              <strong>필수 열</strong>
              <span>학생코드(또는 학번) · 학생이름(또는 이름) · 학년</span>
              <strong>선택 열</strong>
              <span>반 · 이메일</span>
            </div>
            <label className="file-drop">
              <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e)=>chooseFile(e.target.files?.[0])} />
              <strong>{fileName || "CSV 또는 XLSX 파일 선택"}</strong>
              <span>최대 1,000명 · 기존 학생코드가 있으면 중복 생성하지 않습니다.</span>
            </label>

            {importRows.length>0 && (
              <>
                <div className="import-preview">
                  <div className="import-preview-row head"><span>학생코드</span><span>이름</span><span>학년</span><span>반</span><span>이메일</span></div>
                  {importRows.slice(0,12).map((row,index)=>(
                    <div className="import-preview-row" key={index}><span>{row.studentKey}</span><span>{row.displayName}</span><span>{row.gradeLevel}</span><span>{row.classLabel || "-"}</span><span>{row.email || "-"}</span></div>
                  ))}
                </div>
                <button className="primary import-apply" disabled={busy!==""} onClick={applyImport}>{busy==="import"?"동기화 중…":`${importRows.length}명 명단 적용`}</button>
              </>
            )}

            {importResult && <div className="import-result-strip"><b>{importResult.status}</b><span>신규 {importResult.createdCount}</span><span>수정 {importResult.updatedCount}</span><span>재등록 {importResult.reactivatedCount}</span><span>실패 {importResult.failedCount}</span></div>}
          </section>

          <section className="roster-card">
            <div className="panel-title"><strong>최근 가져오기</strong><span>최근 20회</span></div>
            <div className="import-run-list">
              {data.importRuns.map((run)=>(
                <article key={run.runId}><span><strong>{run.sourceType==="XLSX_IMPORT"?"XLSX":"CSV"} · {run.status}</strong><small>{date(run.createdAt)}</small></span><dl><div><dt>요청</dt><dd>{run.requestedCount}</dd></div><div><dt>신규</dt><dd>{run.createdCount}</dd></div><div><dt>수정</dt><dd>{run.updatedCount}</dd></div><div><dt>실패</dt><dd>{run.failedCount}</dd></div></dl></article>
              ))}
              {!data.importRuns.length && <p className="case-empty">가져오기 이력이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="ROSTER" && (
        <div className="roster-plan-stack">
          <section className="roster-card roster-setup-grid">
            <div>
              <div className="panel-title"><strong>1. 다음 학년도</strong><span>없다면 먼저 만듭니다.</span></div>
              {plannedYears.length ? (
                <select value={targetYearId} onChange={(e)=>setTargetYearId(e.target.value)}>{plannedYears.map((year)=><option key={year.academicYearId} value={year.academicYearId}>{year.yearCode} · {year.startsOn}</option>)}</select>
              ) : (
                <div className="year-create-inline"><input value={yearCode} onChange={(e)=>setYearCode(e.target.value)} placeholder="2027" /><input type="date" value={yearStart} onChange={(e)=>setYearStart(e.target.value)} /><input type="date" value={yearEnd} onChange={(e)=>setYearEnd(e.target.value)} /><button className="primary" disabled={busy!=="" || !yearCode || !yearStart || !yearEnd} onClick={createYear}>학년도 만들기</button></div>
              )}
            </div>
            <div>
              <div className="panel-title"><strong>2. 차년도 학급 Master</strong><span>{targetClasses.length}개</span></div>
              <div className="target-class-chips">{targetClasses.map((group)=><span key={group.classGroupId}><b>{group.name}</b>{group.plannedCount}명 예정</span>)}</div>
              <div className="target-class-create"><input type="number" min="1" max="12" value={classGrade} onChange={(e)=>setClassGrade(e.target.value)} placeholder="학년" /><input value={classLabel} onChange={(e)=>setClassLabel(e.target.value)} placeholder="반" /><input value={className} onChange={(e)=>setClassName(e.target.value)} placeholder="학급명(선택)" /><input type="number" min="1" value={classCapacity} onChange={(e)=>setClassCapacity(e.target.value)} placeholder="정원" /><button className="secondary" disabled={busy!=="" || !targetYearId || !classGrade || !classLabel} onClick={createClass}>학급 추가</button></div>
            </div>
            <div>
              <div className="panel-title"><strong>3. 반편성안 생성</strong><span>현재 재학생을 기준으로 만듭니다.</span></div>
              <div className="plan-create-inline"><input value={planName} onChange={(e)=>setPlanName(e.target.value)} placeholder="예: 2027 1차 반편성안" /><button className="primary" disabled={busy!=="" || !targetYearId} onClick={createPlan}>반편성안 만들기</button></div>
            </div>
          </section>

          {data.plans.length>0 && (
            <section className="roster-card">
              <div className="roster-plan-picker"><select value={selectedPlanId} onChange={(e)=>{setSelectedPlanId(e.target.value);setSelectedLearners([]);}}>{data.plans.map((plan)=><option key={plan.planId} value={plan.planId}>{plan.name} · {plan.status}</option>)}</select>{selectedPlan && <span>{readyCount}/{selectedPlan.items.length}명 준비</span>}</div>
            </section>
          )}

          {selectedPlan && (
            <section className="roster-card">
              <header className="roster-plan-head">
                <div><p className="eyebrow">{selectedPlan.sourceYearCode} → {selectedPlan.targetYearCode}</p><h3>{selectedPlan.name}</h3><p>진급은 다음 학년, 재학년은 같은 학년 Class Master에 배정합니다.</p></div>
                <div className="roster-plan-actions">
                  {selectedPlan.status==="DRAFT" && <button className="secondary" disabled={busy!==""} onClick={autoAssign}>같은 반 번호 자동배정</button>}
                  {selectedPlan.status==="DRAFT" && <button className="primary" disabled={busy!=="" || !allReady} onClick={preparePlan}>학년이동 준비안 확정</button>}
                  {selectedPlan.status==="PREPARED" && <button className="primary" onClick={()=>{window.history.pushState({},"","/student-lifecycle");window.dispatchEvent(new PopStateEvent("popstate"));}}>학적관리에서 적용</button>}
                  {["DRAFT","PREPARED"].includes(selectedPlan.status) && <button className="secondary danger" disabled={busy!==""} onClick={()=>run("cancel-plan",()=>client.rpc("center_cancel_roster_plan",{p_school_id:schoolId,p_plan_id:selectedPlan.planId}),"반편성안을 취소했습니다.")}>취소</button>}
                </div>
              </header>

              {selectedPlan.status==="DRAFT" && (
                <div className="roster-bulk-bar">
                  <span>선택 {selectedLearners.length}명</span>
                  <select value={bulkTargetClass} onChange={(e)=>setBulkTargetClass(e.target.value)}><option value="">배정할 반 선택</option>{planClasses.map((group)=><option key={group.classGroupId} value={group.classGroupId}>{group.name}</option>)}</select>
                  <button className="secondary" disabled={busy!=="" || !selectedLearners.length || !bulkTargetClass} onClick={bulkAssign}>선택학생 일괄배정</button>
                </div>
              )}

              <div className="roster-plan-table">
                <div className="roster-plan-row head"><span>선택</span><span>학생</span><span>현재</span><span>다음 배치</span><span>상태</span></div>
                {selectedPlan.items.map((item)=>(
                  <div className="roster-plan-row" key={item.itemId}>
                    <input type="checkbox" disabled={selectedPlan.status!=="DRAFT" || item.action==="GRADUATE"} checked={selectedLearners.includes(item.learnerId)} onChange={()=>setSelectedLearners((current)=>current.includes(item.learnerId)?current.filter((id)=>id!==item.learnerId):[...current,item.learnerId])} />
                    <span><strong>{item.displayName}</strong><small>{item.sourceGradeLevel || "-"}학년 · {item.sourceClassLabel || "-"}반</small></span>
                    <span>{item.action==="GRADUATE"?"졸업 예정":item.action==="REPEAT"?"재학년":"진급"}</span>
                    {selectedPlan.status==="DRAFT" ? (
                      <select value={item.action==="GRADUATE" ? "GRADUATE" : item.targetClassGroupId || ""} onChange={(e)=>setItemTarget(item,e.target.value)}>
                        <option value="">반 미배정</option>
                        {Number(item.sourceGradeLevel)>=12 && <option value="GRADUATE">졸업</option>}
                        {planClasses.filter((group)=>{
                          const source=Number(item.sourceGradeLevel);
                          const target=Number(group.gradeLevel);
                          return target===source || target===source+1;
                        }).map((group)=><option key={group.classGroupId} value={group.classGroupId}>{group.name} · {Number(group.gradeLevel)===Number(item.sourceGradeLevel)?"재학년":"진급"}</option>)}
                      </select>
                    ) : <strong>{item.action==="GRADUATE" ? "졸업" : `${item.targetGradeLevel}학년 ${item.targetClassLabel}반`}</strong>}
                    <b className={item.status==="READY" || item.status==="PREPARED" ? "status-pill good" : "status-pill"}>{item.status==="UNASSIGNED"?"미배정":item.status==="READY"?"준비":"확정"}</b>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </section>
  );
}
